<template>
  <PageHeader :title="title" />
  <v-container density="compact" dense="true">
    <CheckoutProgress
      :checkout-stage="state === 'confirmed' ? 6 : 5"
      :color="state === 'confirmed' ? 'green' : undefined"
    />
    <v-card class="pa-2a ma-2 w-100">
      <v-card-title>Community Boating Order #{{ orderId }}</v-card-title>
      <v-card-text>
        <v-progress-linear v-if="loading && !order" indeterminate />
        <v-alert v-else-if="error" type="error" variant="tonal">
          {{ error }}
          <template #append>
            <v-btn variant="text" @click="load()">Retry</v-btn>
          </template>
        </v-alert>
        <template v-else-if="order">
          <v-alert
            :type="alert.type"
            variant="tonal"
            class="mb-4"
            data-testid="order-status"
          >
            {{ alert.text }}
            <template v-if="state === 'pending' || state === 'unpaid'" #append>
              <v-btn
                v-if="state === 'pending'"
                variant="text"
                :loading="loading"
                @click="load()"
                >Check again</v-btn
              >
              <v-btn v-else variant="text" :to="{ name: 'portal-checkout' }"
                >Return to checkout</v-btn
              >
            </template>
          </v-alert>
          <OrderReceipt
            :order="order"
            :lines="order.lineItems ?? []"
            :item-title="itemTitle"
            :payments="payments"
            :authorized-cents="authorizedCents"
            :line-details="lineDetails"
            purchaser-is-viewer
          />
          <v-alert
            v-if="paymentError"
            type="warning"
            variant="tonal"
            class="mt-3"
            >{{ paymentError }}</v-alert
          >
        </template>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script lang="ts" setup>
import { useOrderService } from "../../../cbi-generator/frontend/composables/useOrderFrontendService";
import type { OrderMember } from "../../../cbi-generator/generated/dto/OrderMember";
import { OrderStatus } from "../../../cbi-generator/generated/dto/OrderStatus";
import { useCatalogDeferred } from "@/composables/shop/useCatalog";
import { errorMessage } from "@/composables/generated/contracts";
import OrderReceipt, {
  type ReceiptLine,
} from "@/components/shop/OrderReceipt.vue";
import { usePaymentService } from "../../../cbi-generator/frontend/composables/usePaymentFrontendService";
import type { PaymentAttempt } from "../../../cbi-generator/generated/dto/PaymentAttempt";
import { PaymentStatus } from "../../../cbi-generator/generated/dto/PaymentStatus";
import {
  loadReceiptLineDetails,
  type ReceiptLineDetails,
} from "@/composables/shop/receiptLineDetails";

type ConfirmationState =
  | "confirmed"
  | "pending"
  | "unpaid"
  | "cancelled"
  | "refunded";

/** Payment settlement can lag the browser; poll briefly before asking the member to check again. */
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 10;

const route = useRoute();
const order = shallowRef<OrderMember | null>(null);
const loading = ref(false);
const error = ref("");
const payments = shallowRef<PaymentAttempt[] | undefined>();
const paymentError = ref("");
const lineDetails = shallowRef<Record<number, ReceiptLineDetails>>({});
const authorizedCents = computed(
  () =>
    payments.value?.reduce(
      (sum, payment) =>
        sum + (payment.status === "CAPTURABLE" ? payment.amountCents : 0),
      0,
    ) ?? 0,
);
const catalog = useCatalogDeferred();
const orderId = computed(() => Number(route.params.id));
let polls = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
let disposed = false;
let version = 0;

const state = computed<ConfirmationState | null>(() => {
  switch (order.value?.orderStatus) {
    case OrderStatus.PROCESSED:
    case OrderStatus.PAID:
      return "confirmed";
    case OrderStatus.PAYING:
    case OrderStatus.CAPTURING:
      return "pending";
    case OrderStatus.OPEN:
      return "unpaid";
    case OrderStatus.CANCELLED:
      return "cancelled";
    case OrderStatus.REFUNDING:
    case OrderStatus.REFUNDED:
    case OrderStatus.PARTIALLY_REFUNDED:
      return "refunded";
    default:
      return null;
  }
});

const alert = computed(() => {
  switch (state.value) {
    case "confirmed":
      return {
        type: "success" as const,
        text: "Thank you! Your order is complete.",
      };
    case "pending":
      return {
        type: "info" as const,
        text: "Your payment is still processing. This page will update when it completes.",
      };
    case "unpaid":
      return {
        type: "warning" as const,
        text: "Payment for this order was not completed. Return to checkout to finish paying.",
      };
    case "cancelled":
      return { type: "error" as const, text: "This order was cancelled." };
    case "refunded":
      return {
        type: "info" as const,
        text: "This order has been refunded in whole or in part.",
      };
    default:
      return {
        type: "warning" as const,
        text: "We could not determine the status of this order.",
      };
  }
});

const title = computed(() =>
  state.value === "confirmed"
    ? "Order Confirmed"
    : state.value === "pending"
      ? "Payment Processing"
      : "Order Status",
);

const itemTitle = (line: ReceiptLine) =>
  catalog.value?.getProductFromItemInstance(
    line.itemInstanceIdTargetIds.assignId,
  )?.title ?? `Catalog item #${line.itemInstanceIdTargetIds.assignId}`;

async function load(auto = false) {
  clearTimeout(timer);
  if (!auto) polls = 0;
  const assignId = orderId.value;
  if (!Number.isSafeInteger(assignId) || assignId <= 0) {
    error.value = "Invalid order reference.";
    return;
  }
  const current = ++version;
  loading.value = true;
  error.value = "";
  try {
    const result = await useOrderService().checkoutOrderMember({ assignId });
    if (disposed || current !== version) return;
    order.value = result;
    paymentError.value = "";
    try {
      const details = await loadReceiptLineDetails({ assignId }, "Member");
      if (disposed || current !== version) return;
      lineDetails.value = details;
    } catch {
      if (disposed || current !== version) return;
      lineDetails.value = {};
    }
    try {
      const attempts = await usePaymentService().paymentAttemptsMember({
        assignId,
      });
      if (disposed || current !== version) return;
      payments.value = attempts.filter(
        (attempt) => attempt.status !== PaymentStatus.CANCELLED,
      );
    } catch (cause) {
      if (disposed || current !== version) return;
      payments.value = undefined;
      paymentError.value = `Payment details could not be loaded: ${errorMessage(cause)}`;
    }
    if (state.value === "pending" && polls++ < MAX_POLLS)
      timer = setTimeout(() => void load(true), POLL_INTERVAL_MS);
  } catch (cause) {
    if (disposed || current !== version) return;
    error.value = errorMessage(cause);
  } finally {
    if (current === version) loading.value = false;
  }
}

watch(orderId, () => {
  order.value = null;
  payments.value = undefined;
  lineDetails.value = {};
  paymentError.value = "";
  void load();
});
onMounted(() => void load());
onBeforeUnmount(() => {
  disposed = true;
  clearTimeout(timer);
});
</script>
